import { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import EngagementTracker from "./components/layout/EngagementTracker";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import EmployeeRoute from "./components/auth/EmployeeRoute";
import ChatWidget from "./components/chat/ChatWidget";

import { publicRoutes } from "./routes/publicRoutes";
import { siteMode } from "./qpi/site";

import PortalLayout from "./pages/portal/PortalLayout";
import PortalHome from "./pages/portal/PortalHome";
import MyAccount from "./pages/portal/MyAccount";
import Orders from "./pages/portal/Orders";
import Invoices from "./pages/portal/Invoices";
import Quotations from "./pages/portal/Quotations";
import Projects from "./pages/portal/Projects";
import Rewards from "./pages/portal/Rewards";

import EmployeesLayout from "./pages/employees/EmployeesLayout";
import EmployeesHome from "./pages/employees/EmployeesHome";
import Deliverables from "./pages/employees/Deliverables";
import OrderBookings from "./pages/employees/OrderBookings";
import Stories from "./pages/employees/Stories";
import StoryDetail from "./pages/employees/StoryDetail";
import NewStory from "./pages/employees/NewStory";
import Directory from "./pages/employees/Directory";
import Profile from "./pages/employees/Profile";
import MySheet from "./pages/employees/MySheet";
import Admin from "./pages/employees/Admin";

// Lazy-loaded so the Firestore SDK they use doesn't weigh down every other page.
const OnlineReferences = lazy(() => import("./pages/OnlineReferences"));
const ImportReferences = lazy(() => import("./pages/references/ImportReferences"));
const AccessLevels = lazy(() => import("./pages/references/AccessLevels"));
// QPi and its admin are separate apps in the same build (see qpi/site.js).
const QpiApp = lazy(() => import("./qpi/QpiApp"));
const AdminApp = lazy(() => import("./qpi/admin/AdminApp"));

const PageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-2 border-qcyan/30 border-t-qcyan animate-spin" />
  </div>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [pathname, hash]);
  return null;
};

const AppRoutes = () => {
  return (
    <>
      <Navbar />
      <ScrollToTop />
      <main className="relative z-10">
        <Routes>
          {publicRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          <Route path="/OnlineReferences" element={<Suspense fallback={<PageSpinner />}><OnlineReferences /></Suspense>} />
          <Route path="/OnlineReferences/import" element={<Suspense fallback={<PageSpinner />}><ImportReferences /></Suspense>} />
          <Route path="/OnlineReferences/access" element={<Suspense fallback={<PageSpinner />}><AccessLevels /></Suspense>} />

          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PortalHome />} />
            <Route path="account" element={<MyAccount />} />
            <Route path="orders" element={<Orders />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="projects" element={<Projects />} />
            <Route path="rewards" element={<Rewards />} />
          </Route>

          <Route
            path="/employees"
            element={
              <EmployeeRoute>
                <EmployeesLayout />
              </EmployeeRoute>
            }
          >
            <Route index element={<EmployeesHome />} />
            <Route path="deliverables" element={<Deliverables />} />
            <Route path="orders" element={<OrderBookings />} />
            <Route path="stories" element={<Stories />} />
            <Route path="stories/new" element={<NewStory />} />
            <Route path="stories/:storyId" element={<StoryDetail />} />
            <Route path="directory" element={<Directory />} />
            <Route path="profile" element={<Profile />} />
            <Route path="my-sheet" element={<MySheet />} />
            <Route path="admin" element={<Admin />} />
          </Route>
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
};

const SiteRoutes = () => {
  const mode = siteMode();
  if (mode === "qpi" || mode === "admin") {
    return (
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/*" element={mode === "qpi" ? <QpiApp base="" /> : <AdminApp />} />
        </Routes>
      </Suspense>
    );
  }
  return (
    <>
      <EngagementTracker />
      <AppRoutes />
    </>
  );
};

function App() {
  return (
    <div className="App relative min-h-screen bg-background text-foreground font-body">
      <AuthProvider>
        <BrowserRouter>
          <SiteRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
